/**
 * 🟥 CUADRO NEGRO Header Mobile (feb-2026) — Menú "Más".
 *
 * Botón visible SOLO en mobile (≤640px) que abre un dropdown vertical
 * con los botones secundarios del header (GitHub, Agenda, Notif, Quota).
 * En tablet/desktop está oculto.
 *
 * 🟪 P25.4 · position: fixed + getBoundingClientRect() para que el
 *   dropdown escape del overflow-hidden del <header>.
 *
 * 🟪 P25.10 (feb-2026) — FIX CRÍTICO: aunque position:fixed debería
 *   escapar del overflow-hidden, el <header> global tiene
 *   `backdrop-blur-md` que CREA UN STACKING CONTEXT propio. Eso
 *   confina position:fixed dentro del ancestor, y el panel queda
 *   invisible aunque técnicamente esté "en pantalla".
 *   Solución: renderizar el panel vía React Portal a document.body,
 *   así escapa de cualquier stacking context ancestor.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

const HeaderMoreMenu = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const compute = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 220;
      const gap = 8;
      let left = rect.left; // alinear a la izquierda del botón (ancla natural)
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }
      if (left < 8) left = 8;
      setPos({ top: rect.bottom + gap, left });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative flex sm:hidden">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Más opciones"
        aria-expanded={open}
        title="Más"
        data-testid="header-more-menu"
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#cfcfcf] hover:text-white hover:bg-[#222] active:scale-95 transition-all"
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {/* 🟪 P25.10 · Portal → renderiza en document.body para escapar
          del stacking context creado por `backdrop-blur-md` del header.
          🟪 P25.12 · NO cerrar el menú al clickear un item. Antes había
          `onClick={() => setOpen(false)}` que desmontaba los items al
          click → los modales renderizados como sibling del item (ej.
          SaveToGitHubModal dentro de GitHubHeaderButton) NUNCA llegaban
          a aparecer. Ahora el menú se cierra solo por clickOutside o
          Escape, dando tiempo al item de disparar su acción completa. */}
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid="header-more-menu-panel"
          className="fixed z-[10000] w-[220px] rounded-xl bg-[#111] border border-[#2a2a2a] shadow-[0_8px_24px_rgba(0,0,0,0.6)] p-2 flex flex-col gap-1"
          style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
        >
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default HeaderMoreMenu;
